import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { animated, useSpring } from 'react-spring';
import { styled } from 'styled-components';
import { flexbox, layout, space } from 'styled-system';
import { onEnterOrSpacePress } from '@hivebrite/utils';
import { Box } from '@design-system/box';
import { LinkSimple } from '@design-system/link';
import { focusOutline, useTheme } from '@design-system/theme';
const AnimatedBox = animated(Box);
export const TAG_DEFAULT_MAX_WIDTH = '220px';
const getTagFont = (variant) => {
    switch (variant) {
        case 'regular':
            return 'body-regular-asphalt';
        case 'bold':
        default:
            return 'body-semibold-asphalt';
    }
};
export const Tag = (_a) => {
    var { href, label, maxWidth = TAG_DEFAULT_MAX_WIDTH, name, onClick, selected = false, variant = 'bold' } = _a, otherProps = __rest(_a, ["href", "label", "maxWidth", "name", "onClick", "selected", "variant"]);
    const theme = useTheme();
    const isClickable = href || onClick;
    const initialStyle = useMemo(() => ({
        backgroundColor: selected ? theme.colors.primary : theme.colors.white,
        borderColor: selected ? theme.colors.primary : theme.colors.horizon,
        color: selected ? theme.colors.white : theme.colors.asphalt,
    }), [selected, theme]);
    const hoverStyle = useMemo(() => ({
        backgroundColor: theme.colors.primaryDark,
        borderColor: theme.colors.primaryDark,
        color: theme.colors.white,
    }), [theme]);
    const [spring, springApi] = useSpring(() => initialStyle);
    const onMouseLeave = useCallback(() => springApi.start(initialStyle), [initialStyle, springApi]);
    const onMouseEnter = useCallback(() => springApi.start(hoverStyle), [hoverStyle, springApi]);
    const handleOnClick = useMemo(() => onClick &&
        ((event) => {
            event.preventDefault();
            onClick(name);
        }), [name, onClick]);
    const handleKeyPress = useMemo(() => (handleOnClick ? onEnterOrSpacePress(handleOnClick) : undefined), [handleOnClick]);
    useEffect(() => {
        springApi.start(initialStyle);
    }, [initialStyle, selected, springApi]);
    const commonProps = {
        $isClickable: isClickable,
        onMouseEnter,
        onMouseLeave,
        selected,
        style: spring,
    };
    return href ? (_jsx(LinkSimple, { style: { display: 'inline-block' }, to: href, children: _jsx(TagWrapper, Object.assign({}, commonProps, { maxWidth: maxWidth, variant: variant }, otherProps, { children: label !== null && label !== void 0 ? label : name })) })) : (_jsx(TagWrapper, Object.assign({}, commonProps, { maxWidth: maxWidth, onClick: handleOnClick, onKeyPress: handleKeyPress, tabIndex: isClickable ? 0 : -1, variant: variant }, otherProps, { children: label !== null && label !== void 0 ? label : name })));
};
const getBorder = (theme) => `1px solid ${theme.colors.horizon}`;
const TagWrapper = styled(AnimatedBox).attrs(({ maxWidth, variant }) => ({
    font: getTagFont(variant),
    maxWidth,
    minHeight: '32px',
    pb: '6px',
    pt: '7px',
    px: 3,
})) `
  ${flexbox}
  ${focusOutline}
  ${space}
  ${layout}
  align-items: center;
  border-radius: 5px;
  border: ${({ theme }) => getBorder(theme)};
  display: block;
  justify-content: center;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;
  cursor: ${(props) => (props.$isClickable ? 'pointer' : 'auto')};
  pointer-events: ${(props) => (props.$isClickable ? 'all' : 'none')};
`;
//# sourceMappingURL=Tag.js.map